/**
 * Положение стопки стикеров на экране — чистые правила и память.
 *
 * Владелец (22.09): «чтобы её можно было перемещать по экрану, чтобы вдруг не
 * мешала». Стопку тянут за любой стикер; после отпускания она прилипает к
 * ближайшему краю (левому или правому) с прежним отступом и держит вертикаль —
 * как плавающие кнопки в iOS: посреди экрана она мешала бы сильнее, чем у края.
 * Положение — настройка вида, не данные: живёт в localStorage этого устройства.
 */
export type StickSide = 'left' | 'right'

export interface StickersPos {
	side: StickSide
	/** Верх стопки в px от верха окна. */
	top: number
}

/** Сдвиг, после которого нажатие считается перетаскиванием, а не кликом. */
export const DRAG_THRESHOLD = 6

const KEY = 'umnik.stickers_pos'

export interface Bounds {
	vw: number
	vh: number
	/** Габарит стопки. */
	width: number
	height: number
	/** Отступ от краёв (тот же, что у положения по умолчанию). */
	inset: number
	/** Ниже этой линии стопка не поднимается — под шапкой ей не место. */
	minTop: number
}

export function snapSide(centerX: number, vw: number): StickSide {
	return centerX < vw / 2 ? 'left' : 'right'
}

export function clampTop(top: number, b: Pick<Bounds, 'vh' | 'height' | 'inset' | 'minTop'>): number {
	const max = Math.max(b.minTop, b.vh - b.height - b.inset)
	return Math.round(Math.min(Math.max(top, b.minTop), max))
}

export function clampLeft(left: number, b: Pick<Bounds, 'vw' | 'width' | 'inset'>): number {
	const max = Math.max(b.inset, b.vw - b.width - b.inset)
	return Math.round(Math.min(Math.max(left, b.inset), max))
}

/** Куда лечь после отпускания: сторона — по центру стопки, верх — в пределах экрана. */
export function settle(rect: { left: number; top: number; width: number }, b: Bounds): StickersPos {
	return { side: snapSide(rect.left + rect.width / 2, b.vw), top: clampTop(rect.top, b) }
}

export function readPos(): StickersPos | null {
	try {
		const raw = localStorage.getItem(KEY)
		if (!raw) return null
		const p = JSON.parse(raw) as Partial<StickersPos> | null
		if (!p || (p.side !== 'left' && p.side !== 'right') || typeof p.top !== 'number' || !Number.isFinite(p.top)) return null
		return { side: p.side, top: p.top }
	} catch {
		return null
	}
}

export function writePos(p: StickersPos | null): void {
	try {
		if (p) localStorage.setItem(KEY, JSON.stringify(p))
		else localStorage.removeItem(KEY)
	} catch {
		/* нет хранилища — стопка просто вернётся на место после перезагрузки */
	}
}
